import type {
  Dispatch,
  GlobalModal,
  GlobalModalKeys,
  State,
} from '../use_candidate_profile';
import {
  TabsByIndexType,
  tabsKeyedByIndex,
  tabsKeyedByName,
} from '../use_candidate_profile';
import {
  standardRequest,
  standardRequestWithErrorFlash,
} from '../../../shared/utils/request';
import {
  Header,
  HeaderSource,
  ProspectForJobDepartmentAndOfficeResponse,
  ProspectForLabel,
  TaggedOption,
} from '../../shared/types';
import {
  getNoteAnchor,
  rightBarOpenKey,
  rightBarTabKey,
} from '../../shared/utils';
import { format } from 'date-fns';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('candidate_profile_redesign');

export const currentTab: () => any = () => {
  const anchor = window.location.hash.substring(1);
  const validAnchors = Object.keys(tabsKeyedByName);

  return validAnchors.includes(anchor) ? anchor : validAnchors[0];
};

type SetTabAction = {
  type: 'setTab';
  payload: {
    index: number;
  };
};

export const changeTab =
  (state: State, dispatch: Dispatch) => (index: number) => {
    window.history.replaceState(
      '',
      '',
      `#${tabsKeyedByIndex[index as keyof TabsByIndexType]}`
    );
    dispatch({ type: 'setTab', payload: { index } });
  };

export type RightBarTabOption =
  | 'candidateDetails'
  | 'appDetails'
  | 'allJobsAndApplications'
  | 'notes'
  | 'alertsReminders'
  | 'securityScreening'
  | 'agentChat';

type SetRightBarTabAction = {
  type: 'setRightBarTab';
  payload: {
    tab: RightBarTabOption;
  };
};
export type ChangeRightBarTabOptions = {
  tab: RightBarTabOption;
  scrollToSelector?: string;
  collapsibleSectionSelector?: string;
};

const throttle = () => new Promise<void>(async (res) => setTimeout(res, 200));

const waitForSelector = async (
  selector: string | undefined,
  // eslint-disable-next-line no-shadow
  callback: (selector: string, elem: HTMLElement) => void
) => {
  if (!selector) {
    return;
  }

  let count = 0;

  while (count < 1000) {
    const elem = document.querySelector<HTMLElement>(selector);
    if (!elem) {
      count++;
      await throttle();
      continue;
    }

    callback(selector, elem);

    break;
  }
};

const expandCollapsibleSection = async (
  selector: string,
  elem: HTMLElement
) => {
  elem.scrollIntoView({
    behavior: 'smooth',
  });

  const sectionIsClosed =
    document.querySelector(selector + ' .ReactCollapse--collapse')
      ?.ariaHidden === 'true';
  if (sectionIsClosed) {
    const collapseButton = document.querySelector(
      selector + ' button'
    ) as HTMLElement;
    collapseButton.click();
  }
};

const scrollAndFocusSelector = async (selector: string, elem: HTMLElement) => {
  elem.scrollIntoView({
    behavior: 'smooth',
  });

  // calling focus doesn't add default browser focus styles
  elem.classList.add('focusable');
  elem.style.background = FullPalette.IRIS_E25;
  elem.focus({ preventScroll: true });
};

export const changeRightBarTab =
  (state: State, dispatch: Dispatch) =>
  ({
    tab,
    scrollToSelector,
    collapsibleSectionSelector,
  }: ChangeRightBarTabOptions) => {
    const { anchorType } = getNoteAnchor();
    localStorage.setItem(rightBarTabKey, tab);
    localStorage.setItem(rightBarOpenKey, true.toString());

    const { currentRightBarTab } = state;
    if (currentRightBarTab === tab && !scrollToSelector) {
      changeRightBarTabOpen(state, dispatch)(false);
    } else {
      dispatch({ type: 'setRightBarTab', payload: { tab } });
    }

    if (anchorType === 'note') {
      window.history.replaceState('', '', window.location.pathname);
    }

    const currentFocusableElements = Array.from(
      document.querySelectorAll('[data-provides=rightbar-body] .focusable')
    ) as HTMLElement[];
    currentFocusableElements.forEach((elem) => {
      elem.classList.remove('focusable');
      elem.style.background = '';
    });

    // just leave this promise hanging, it'll resolve eventually
    new Promise<void>(async (res, rej) => {
      try {
        waitForSelector(collapsibleSectionSelector, expandCollapsibleSection);
        waitForSelector(scrollToSelector, scrollAndFocusSelector);
        res();
      } catch (e) {
        rej(e);
      }
    });
  };

type SetRightBarTabOpenAction = {
  type: 'setRightBarTabOpen';
  payload: {
    open: boolean;
  };
};

export const changeRightBarTabOpen =
  (_: State, dispatch: Dispatch) => (open: boolean) => {
    if (open) {
      const tab = (localStorage.getItem(rightBarTabKey) ??
        'candidateDetails') as RightBarTabOption;
      dispatch({ type: 'setRightBarTab', payload: { tab } });
      localStorage.setItem(rightBarTabKey, tab);
    }

    localStorage.setItem(rightBarOpenKey, open.toString());
    dispatch({ type: 'setRightBarTabOpen', payload: { open } });
  };

type SetModalAction = {
  type: 'setGlobalModal';
  payload: {
    modal: GlobalModalKeys;
    state: boolean;
    props?: GlobalModalProps;
  };
};

type GlobalModalProps = GlobalModal[GlobalModalKeys][1];

export type UseGlobalModalReturnType = {
  isOpen: boolean;
  openModal: (incomingProps?: GlobalModalProps) => void;
  closeModal: () => void;
  props: GlobalModalProps;
};

export const useGlobalModal =
  (state: State, dispatch: Dispatch) =>
  (modalName: GlobalModalKeys): UseGlobalModalReturnType => {
    const [isOpen, props] = state.globalModal[modalName];

    const openModal = (incomingProps?: GlobalModalProps) =>
      dispatch({
        type: 'setGlobalModal',
        payload: { modal: modalName, state: true, props: incomingProps },
      });
    const closeModal = () =>
      dispatch({
        type: 'setGlobalModal',
        payload: { modal: modalName, state: false },
      });

    return { isOpen, openModal, closeModal, props };
  };

type SetHeaderAction = {
  type: 'setHeader';
  payload: {
    header: Header;
  };
};

export const refreshHeader = (state: State, dispatch: Dispatch) => async () => {
  const result = await standardRequest<Header>(state.paths.refreshHeaderPath);
  dispatch({ type: 'setHeader', payload: { header: result.data } });
};

type SetHeaderSourceAction = {
  type: 'setHeaderSource';
  payload: {
    source: HeaderSource;
  };
};

export const refreshHeaderSource =
  (state: State, dispatch: Dispatch) => async () => {
    const result = await standardRequest<HeaderSource>(
      state.paths.refreshHeaderSourcePath
    );
    dispatch({ type: 'setHeaderSource', payload: { source: result.data } });
  };

type SetHeaderDepartmentAndOfficeAction = {
  type: 'setHeaderDepartmentAndOffice';
  payload: {
    office: Item | TaggedOption | null;
    department: Item | TaggedOption | null;
  };
};

export const setHeaderDepartmentAndOffice =
  (_: State, dispatch: Dispatch) =>
  (
    payload: Pick<
      ProspectForJobDepartmentAndOfficeResponse,
      'office' | 'department'
    >
  ) => {
    const office = formatProspectiveDepartmentAndOffice(payload.office);
    const department = formatProspectiveDepartmentAndOffice(payload.department);

    dispatch({
      type: 'setHeaderDepartmentAndOffice',
      payload: { office, department },
    });
  };

const formatProspectiveDepartmentAndOffice = (
  item: ProspectForLabel | null
): TaggedOption | null => {
  if (!item) {
    return null;
  }

  return {
    value: item.id.toString(),
    label: item.name,
    tag: item.tag,
  };
};

export const requestConsent =
  ({ header }: State, dispatch: Dispatch) =>
  async (url: string) => {
    const response = await standardRequestWithErrorFlash.post(url, null, {
      successMessage: t('header.gdpr_banners.request_consent_success'),
    });

    dispatch({
      type: 'setHeader',
      payload: {
        header: {
          ...header,
          gdprBanner: {
            ...header.gdprBanner,
            explicitConsent: {
              ...response.data.props,
              sentConsentRequest: true,
            },
          },
        },
      },
    });
  };

export const deleteGdprData =
  ({ header: { gdprBanner } }: State) =>
  async (dataRetentionRuleId?: string | null) => {
    const params: Record<string, unknown> = {};
    if (dataRetentionRuleId) {
      params.data_retention_rule_id = dataRetentionRuleId;
    }

    const deletePath =
      gdprBanner.displayMode === 'explicit_consent'
        ? gdprBanner.explicitConsent.deleteDataPath
        : gdprBanner.retentionRule.delete_personal_data_path;

    await standardRequestWithErrorFlash.delete(deletePath, {
      params: params,
      successMessage: t('header.gdpr_banners.delete_data_success'),
      errorMessage: t('header.gdpr_banners.delete_data_error'),
    });

    window.location.reload();
  };

export const updateDataDeletion =
  (state: State, dispatch: Dispatch) => async (date: number) => {
    const {
      currentUser: { date_format_birch_datepicker },
      header: {
        gdprBanner: {
          retentionRule: { update_deletion_date_path, person_id },
        },
      },
    } = state;

    const response = await standardRequestWithErrorFlash.put(
      update_deletion_date_path,
      {
        person_id,
        date: format(date, date_format_birch_datepicker),
      },
      { successMessage: t('header.gdpr_banners.edit_delete_date_success') }
    );

    dispatch({
      type: 'setHeader',
      payload: {
        header: {
          ...state.header,
          gdprBanner: response.data,
        },
      },
    });
  };

export type AllApplicationsRightbar = { [k: string]: boolean };

type SetAllApplicationsRightbarAction = {
  type: 'setAllApplicationsRightbar';
  payload: AllApplicationsRightbar;
};

export const setAllApplicationsRightbar =
  (_state: State, dispatch: Dispatch) =>
  async (expandedState: AllApplicationsRightbar) => {
    dispatch({
      type: 'setAllApplicationsRightbar',
      payload: expandedState,
    });
  };

export type Actions =
  | SetTabAction
  | SetRightBarTabAction
  | SetModalAction
  | SetHeaderAction
  | SetHeaderSourceAction
  | SetHeaderDepartmentAndOfficeAction
  | SetRightBarTabOpenAction
  | SetAllApplicationsRightbarAction;
